import { textColorFields } from '../_shared/textColor.js';
import { animationFields } from '../_shared/universal.js';
import { patternFields } from '../_shared/pattern.js';
import { columnBlockSchema } from '../../blocks/column/index.js';
import { headingBlockSchema } from '../../blocks/heading/index.js';
import { textBlockSchema } from '../../blocks/text/index.js';
import { imageBlockSchema } from '../../blocks/image/index.js';
import { iconWithContentBlockSchema } from '../../blocks/icon-with-content/index.js';
/**
 * custom-columns-new — Shrine "Custom columns V2" (`sections/custom-columns-new.liquid`), ported
 * config-by-config. A FLEXIBLE 12-part grid: the section takes repeatable `column` blocks, and every column
 * spans N of the 12 desktop parts (4 mobile parts) and holds its OWN nested content blocks. This is the
 * distinguishing engine vs the uniform `custom-columns` (Multicolumn): here the layout is bespoke per column.
 *
 * The section owns the Shrine band knobs: an optional anchor id, visibility, the colour scheme (incl. the
 * `custom` override cluster: bg / gradient / image / text / button colours), a background pattern, the
 * column-indicator overlay, desktop + mobile grid gaps + vertical alignment, the desktop page width
 * (narrow / normal / full / full-no-padding / custom), mobile full-width, and section padding. The 12-part
 * grid CSS + column chrome (border / bg / shadow / padding) are ported verbatim from Shrine `base.css`.
 */
const VISIBILITY_OPTIONS = [
    { value: 'always-display', label: 'All devices' },
    { value: 'desktop-hidden', label: 'Mobile only' },
    { value: 'mobile-hidden', label: 'Desktop only' },
];
const COLOR_SCHEME_OPTIONS = [
    { value: 'accent-1', label: 'Accent 1' },
    { value: 'accent-2', label: 'Accent 2' },
    { value: 'background-1', label: 'Background 1' },
    { value: 'background-2', label: 'Background 2' },
    { value: 'inverse', label: 'Inverse' },
    { value: 'custom', label: 'Custom' },
];
const VERTICAL_ALIGN_OPTIONS = [
    { value: 'flex-start', label: 'Top' },
    { value: 'center', label: 'Center' },
    { value: 'flex-end', label: 'Bottom' },
];
const PAGE_WIDTH_OPTIONS = [
    { value: 'page-width--narrow', label: 'Narrow' },
    { value: 'normal', label: 'Normal' },
    { value: 'desktop-full-page', label: 'Full width' },
    { value: 'desktop-full-page-no-padding', label: 'Full width without side spacing' },
    { value: 'custom', label: 'Custom' },
];
/** Shrine's default column-indicator colour cycle (`border_colors_arr`) — used by the grid-line overlay. */
export const INDICATOR_COLORS = [
    '#FF0000',
    '#00FF00',
    '#0000FF',
    '#FFD800',
    '#FF00FF',
    '#00FFFF',
];
export const customColumnsNewSchema = {
    type: 'custom-columns-new',
    name: 'Custom Columns V2',
    version: 1,
    templates: ['index', 'collection', 'page', 'product', 'thank-you'],
    // Only `column` is addable at the section's top level (Shrine `blocks: [{ "type": "column" }]`).
    accepts: ['column'],
    // The block SCHEMAS the recursive instantiator needs to seed the preset tree + the builder needs to add
    // them: the container `column` plus every descendant type it nests (`instantiateBlockNode` resolves each
    // against this list at every depth).
    blocks: [
        columnBlockSchema,
        headingBlockSchema,
        textBlockSchema,
        imageBlockSchema,
        iconWithContentBlockSchema,
    ],
    maxBlocks: 6,
    fields: [
        {
            id: 'displaySectionId',
            type: 'checkbox',
            label: 'Display section ID',
            info: 'Adds an anchor id to this section so a button link can scroll to it.',
            default: false,
        },
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: VISIBILITY_OPTIONS,
        },
        // ── General (colour scheme + custom override cluster + pattern). ─────────────────────────────────
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: 'General',
            default: 'background-1',
            options: COLOR_SCHEME_OPTIONS,
        },
        {
            id: 'customColorsBackground',
            type: 'color',
            label: 'Custom background color',
            group: 'General',
            default: { ref: 'custom', value: '#FFFFFF' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customGradientBackground',
            type: 'gradient',
            label: 'Custom gradient background',
            group: 'General',
            info: 'If added, replaces the background color when applicable.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customImageBackground',
            type: 'image',
            label: 'Custom image background',
            group: 'General',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customMobileImageBackground',
            type: 'image',
            label: 'Custom mobile image background',
            group: 'General',
            info: 'If empty, the custom image background is also used on mobile.',
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsText',
            type: 'color',
            label: 'Custom text color',
            group: 'General',
            default: { ref: 'custom', value: '#2E2A39' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        // Shrine's custom-scheme BUTTON colours (`--color-button` / `--color-button-text` /
        // `--color-base-outline-button-labels`). A `column` here can nest a `button` block, so — exactly like
        // Shrine — the custom scheme sets these three vars on the section root and any nested button picks them up.
        {
            id: 'customColorsSolidButtonBackground',
            type: 'color',
            label: 'Custom solid button background',
            group: 'General',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsSolidButtonText',
            type: 'color',
            label: 'Custom solid button label color',
            group: 'General',
            default: { ref: 'custom', value: '#ffffff' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsOutlineButton',
            type: 'color',
            label: 'Custom outline button color',
            group: 'General',
            default: { ref: 'custom', value: '#dd1d1d' },
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        // ── Background pattern (shared Shrine engine). ───────────────────────────────────────────────────
        ...patternFields(),
        {
            id: 'displayGridLines',
            type: 'checkbox',
            label: 'Display indicators of each column',
            info: 'Overlays a numbered, coloured outline on every column while you design the layout.',
            default: false,
        },
        // ── Desktop layout. ──────────────────────────────────────────────────────────────────────────────
        {
            id: 'desktopPageWidth',
            type: 'select',
            label: 'Page width',
            group: 'Desktop layout',
            default: 'normal',
            options: PAGE_WIDTH_OPTIONS,
        },
        {
            id: 'customPageWidth',
            type: 'number',
            label: 'Custom page width',
            group: 'Desktop layout',
            default: 900,
            unit: 'px',
            visibleWhen: { field: 'desktopPageWidth', equals: 'custom' },
        },
        {
            id: 'columnGapDesktop',
            type: 'range',
            label: 'Horizontal spacing',
            group: 'Desktop layout',
            min: 0,
            max: 120,
            step: 4,
            unit: 'px',
            default: 40,
        },
        {
            id: 'rowGapDesktop',
            type: 'range',
            label: 'Vertical spacing',
            group: 'Desktop layout',
            min: 0,
            max: 80,
            step: 4,
            unit: 'px',
            default: 40,
        },
        {
            id: 'desktopVerticalAlignment',
            type: 'select',
            label: 'Vertical alignment',
            group: 'Desktop layout',
            default: 'center',
            options: VERTICAL_ALIGN_OPTIONS,
        },
        // ── Mobile layout. ───────────────────────────────────────────────────────────────────────────────
        {
            id: 'mobileFullPage',
            type: 'checkbox',
            label: 'Full page width',
            group: 'Mobile layout',
            default: false,
        },
        {
            id: 'columnGapMobile',
            type: 'range',
            label: 'Horizontal spacing',
            group: 'Mobile layout',
            min: 0,
            max: 50,
            step: 2,
            unit: 'px',
            default: 20,
        },
        {
            id: 'rowGapMobile',
            type: 'range',
            label: 'Vertical spacing',
            group: 'Mobile layout',
            min: 0,
            max: 50,
            step: 2,
            unit: 'px',
            default: 30,
        },
        {
            id: 'mobileVerticalAlignment',
            type: 'select',
            label: 'Vertical alignment',
            group: 'Mobile layout',
            default: 'flex-start',
            options: VERTICAL_ALIGN_OPTIONS,
        },
        // ── Section padding. ─────────────────────────────────────────────────────────────────────────────
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: 'Section padding',
            min: 0,
            max: 100,
            step: 4,
            unit: 'px',
            default: 36,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
};
/** The exact Shrine `custom-columns-new` preset: a full-width heading + text row, then three columns. */
export const customColumnsNewPresets = [
    {
        name: 'Custom Columns V2',
        settings: {
            colorScheme: 'background-1',
            paddingTop: 36,
            paddingBottom: 36,
        },
        blocks: [
            {
                type: 'column',
                settings: { desktopWidth: 12, mobileWidth: 4 },
                blocks: [
                    {
                        type: 'heading',
                        settings: {
                            title: { format: 'html', html: 'Custom columns' },
                            headingSize: 'h1',
                            alignment: 'center',
                        },
                    },
                    {
                        type: 'text',
                        settings: {
                            text: {
                                format: 'html',
                                html: '<p>Use the flexible grid system to create any layout. Each block can be placed in any of the columns.</p>',
                            },
                            alignment: 'center',
                        },
                    },
                ],
            },
            {
                type: 'column',
                settings: { desktopWidth: 4, mobileWidth: 4 },
                blocks: [
                    {
                        type: 'icon-with-content',
                        settings: {
                            title: '@t:section.icon_with_text',
                            text: {
                                format: 'html',
                                html: '<p>Pair text with an icon to focus on your chosen product, collection, or blog post</p>',
                            },
                            marginTop: 0,
                            marginBottom: 30,
                        },
                    },
                    {
                        type: 'icon-with-content',
                        settings: {
                            title: '@t:section.icon_with_text',
                            text: {
                                format: 'html',
                                html: '<p>Pair text with an icon to focus on your chosen product, collection, or blog post</p>',
                            },
                            marginTop: 15,
                            marginBottom: 0,
                        },
                    },
                ],
            },
            {
                type: 'column',
                settings: { desktopWidth: 4, mobileWidth: 4 },
                blocks: [{ type: 'image', settings: {} }],
            },
            {
                type: 'column',
                settings: { desktopWidth: 4, mobileWidth: 4 },
                blocks: [
                    {
                        type: 'icon-with-content',
                        settings: {
                            title: '@t:section.icon_with_text',
                            text: {
                                format: 'html',
                                html: '<p>Pair text with an icon to focus on your chosen product, collection, or blog post</p>',
                            },
                            marginTop: 0,
                            marginBottom: 30,
                        },
                    },
                    {
                        type: 'icon-with-content',
                        settings: {
                            title: '@t:section.icon_with_text',
                            text: {
                                format: 'html',
                                html: '<p>Pair text with an icon to focus on your chosen product, collection, or blog post</p>',
                            },
                            marginTop: 15,
                            marginBottom: 0,
                        },
                    },
                ],
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const customColumnsNewMeta = {
    label: 'Custom Columns V2',
    group: 'Content',
    icon: 'columns',
};
//# sourceMappingURL=schema.js.map